import { useState } from 'react'
import { Switch } from '@/components/ui/Switch'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'

export type SupportHoursSchedule = Record<string, { open: string; close: string }>

const DAYS: Array<{ key: string; label: string }> = [
  { key: 'mon', label: 'Monday' },
  { key: 'tue', label: 'Tuesday' },
  { key: 'wed', label: 'Wednesday' },
  { key: 'thu', label: 'Thursday' },
  { key: 'fri', label: 'Friday' },
  { key: 'sat', label: 'Saturday' },
  { key: 'sun', label: 'Sunday' },
]

const DEFAULT_OPEN = '09:00'
const DEFAULT_CLOSE = '17:00'

interface SupportHoursEditorProps {
  value: SupportHoursSchedule | null
  onSave: (schedule: SupportHoursSchedule | null) => void
  isSubmitting?: boolean
  // Showing the parent (account) schedule under the editor lets agents see
  // what they're overriding.
  inheritedFrom?: SupportHoursSchedule | null
  inheritedLabel?: string
}

// Compare two schedules independently of JSON key order — the server can
// return days in any order and we don't want to flag the editor as dirty on
// mount just because the keys happen to differ.
function isDirty(a: SupportHoursSchedule, b: SupportHoursSchedule): boolean {
  const aKeys = Object.keys(a).sort()
  const bKeys = Object.keys(b).sort()
  if (aKeys.length !== bKeys.length) return true
  for (let i = 0; i < aKeys.length; i++) {
    if (aKeys[i] !== bKeys[i]) return true
    const key = aKeys[i]
    if (a[key].open !== b[key].open || a[key].close !== b[key].close) return true
  }
  return false
}

export function SupportHoursEditor({ value, onSave, isSubmitting, inheritedFrom, inheritedLabel }: SupportHoursEditorProps) {
  const [draft, setDraft] = useState<SupportHoursSchedule>(value ?? {})

  const setDayOpen = (day: string, open: boolean) => {
    setDraft((prev) => {
      const next = { ...prev }
      if (open) {
        next[day] = next[day] ?? { open: DEFAULT_OPEN, close: DEFAULT_CLOSE }
      } else {
        delete next[day]
      }
      return next
    })
  }

  const setTime = (day: string, field: 'open' | 'close', val: string) => {
    setDraft((prev) => ({
      ...prev,
      [day]: { ...(prev[day] ?? { open: DEFAULT_OPEN, close: DEFAULT_CLOSE }), [field]: val },
    }))
  }

  const dirty = isDirty(draft, value ?? {})
  // SupportHours.normalize silently drops days where close <= open. Block
  // the save and call it out inline so the user doesn't lose the day on
  // the roundtrip without explanation.
  const invalidDays = DAYS.filter(({ key }) => draft[key] && draft[key].close <= draft[key].open).map((d) => d.key)
  const hasInvalidDay = invalidDays.length > 0

  const handleSave = () => {
    const hasAny = Object.keys(draft).length > 0
    onSave(hasAny ? draft : null)
  }

  const handleClear = () => {
    setDraft({})
  }

  return (
    <div className="space-y-3">
      <div className="rounded-md border border-border divide-y divide-border">
        {DAYS.map(({ key, label }) => {
          const day = draft[key]
          const enabled = !!day
          const invalid = enabled && day.close <= day.open
          return (
            <div key={key} className="grid grid-cols-[110px_60px_1fr] items-center gap-3 p-3">
              <span className="text-sm font-medium">{label}</span>
              <Switch checked={enabled} onCheckedChange={(open) => setDayOpen(key, open)} />
              {enabled ? (
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-2 text-sm">
                    <Input
                      type="time"
                      value={day?.open ?? DEFAULT_OPEN}
                      onChange={(e) => setTime(key, 'open', e.target.value)}
                      className="w-28"
                    />
                    <span className="text-muted-foreground">to</span>
                    <Input
                      type="time"
                      value={day?.close ?? DEFAULT_CLOSE}
                      onChange={(e) => setTime(key, 'close', e.target.value)}
                      className="w-28"
                    />
                  </div>
                  {invalid && (
                    <span className="text-xs text-rose-500 dark:text-rose-400">
                      Close must be after open.
                    </span>
                  )}
                </div>
              ) : (
                <span className="text-sm text-muted-foreground">Closed</span>
              )}
            </div>
          )
        })}
      </div>

      {inheritedFrom && inheritedLabel && (
        <p className="text-xs text-muted-foreground">
          When cleared, falls back to <span className="font-medium">{inheritedLabel}</span>.
        </p>
      )}

      <div className="flex items-center gap-2">
        <Button type="button" onClick={handleSave} disabled={!dirty || isSubmitting || hasInvalidDay} className="cursor-pointer">
          {isSubmitting ? 'Saving…' : 'Save support hours'}
        </Button>
        {Object.keys(draft).length > 0 && (
          <Button type="button" variant="outline" onClick={handleClear} className="cursor-pointer">
            Clear all
          </Button>
        )}
      </div>
    </div>
  )
}
